import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { BehaviorSubject } from "rxjs";
import { useObservable } from "../../rxsupport";
import { FatOutlineButton } from "./FatOutlineButton";

// Types and interfaces

export type DialogButtonColor = 'green' | 'atlantis' | 'black' | 'red';

export type DialogButton = {
  text: string;
  color: DialogButtonColor;
  onClick: () => void;
  minWidth?: string;
  data?: any;
};

export type DialogConfig = {
  id: string;
  title: string;
  content: React.ReactNode;
  buttons: DialogButton[];
  condition: boolean;
  showCancel?: boolean; // Optional: defaults to true
};

export type SequentialDialogProps = {
  show$: BehaviorSubject<boolean>;
  dialogs: DialogConfig[];
  onCancel: () => void;
  onComplete: (collectedData: DialogStepData[]) => void;
};

export type DialogStepData = {
  dialogId: string;
  buttonText: string;
  buttonData?: any;
  originalAction?: () => void;
};

// Utility functions

const createStepData = (
  dialogId: string,
  buttonText: string,
  buttonData?: any,
  originalAction?: () => void
): DialogStepData => ({
  dialogId,
  buttonText,
  buttonData,
  originalAction
});


// Main component

export const SequentialDialog = memo(({
  show$,
  dialogs,
  onCancel,
  onComplete
}: SequentialDialogProps) => {
  const show = useObservable(show$, false);
  const [activeDialogIndex, setActiveDialogIndex] = useState(0);
  const [collectedData, setCollectedData] = useState<DialogStepData[]>([]);

  // Derived state
  // Note: `dialogs` is a new array reference on every parent render, so it must
  // not be used as a useEffect dependency that triggers setState (that caused an
  // infinite update loop). Deriving via useMemo avoids extra re-renders.
  const activeDialogs = useMemo(
    () => dialogs.filter(dialog => dialog.condition),
    [dialogs]
  );

  // State management

  const prevShowRef = useRef(false);
  useEffect(() => {
    if (show && !prevShowRef.current && activeDialogs.length > 0) {
      setActiveDialogIndex(0);
      setCollectedData([]);
    }
    prevShowRef.current = show;
  }, [show, activeDialogs.length]);

  // Dialog navigation

  const moveToNextDialog = (stepData?: DialogStepData): boolean => {
    if (stepData) {
      setCollectedData(prev => [...prev, stepData]);
    }
    
    if (activeDialogIndex < activeDialogs.length - 1) {
      setActiveDialogIndex(activeDialogIndex + 1);
      return true;
    }
    
    const finalData = stepData ? [...collectedData, stepData] : collectedData;
    onComplete(finalData);
    return false;
  };
  
  const isLastDialog = activeDialogIndex === activeDialogs.length - 1;

  // Early returns

  if (!show || activeDialogs.length === 0 || activeDialogIndex >= activeDialogs.length) {
    return <div />;
  }

  const currentDialog = activeDialogs[activeDialogIndex];
  const showCancel = currentDialog.showCancel !== false; // Default to true if not specified
  
  // Button handling

  const createWrappedButton = (button: DialogButton): DialogButton => ({
    ...button,
    onClick: () => {
      const stepData = createStepData(
        currentDialog.id,
        button.text,
        button.data,
        isLastDialog ? button.onClick : undefined
      );

      // A cancel decision ends the sequence. Later dialogs will not be shown.
      if (button.data?.action === 'cancel') {
        onCancel();
        show$.next(false);
        return;
      }
      
      moveToNextDialog(stepData);
      
      if (isLastDialog) {
        show$.next(false);
      }
    }
  });

  const wrappedButtons = currentDialog.buttons.map(createWrappedButton);

  // Render

  return (
    <div className="overflow-y-auto overflow-x-hidden absolute z-50 justify-center items-center h-modal h-full inset-0"
         style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
      <div className="relative px-4 w-full max-w-2xl h-full md:h-auto"
           style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          
          {/* Dialog Header */}
          <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {currentDialog.title}
            </h3>
            {activeDialogs.length > 1 && (
              <div className="text-sm text-gray-500">
                Dialog {activeDialogIndex + 1} of {activeDialogs.length}
              </div>
            )}
          </div>
          
          {/* Dialog Content */}
          <div className="space-y-6">
            <div className="flex flex-col w-full p-4">
              {currentDialog.content}
            </div>
          </div>

          {/* Dialog Actions */}
          <div className="w-full border-t dark:border-gray-600 px-6 py-4">
            <div className="flex flex-col items-center gap-4">
              {wrappedButtons.length === 1 ? (
                // Single primary button + cancel - both on same line
                <div className="flex gap-4 justify-center">
                  <FatOutlineButton
                    key={`${currentDialog.id}-action-0`}
                    color={wrappedButtons[0].color}
                    text={wrappedButtons[0].text}
                    minWidth={wrappedButtons[0].minWidth || "140px"}
                    onClick={wrappedButtons[0].onClick}
                  />
                  {showCancel && (
                    <FatOutlineButton
                      color="red"
                      text="Cancel"
                      minWidth="120px"
                      onClick={onCancel}
                    />
                  )}
                </div>
              ) : wrappedButtons.length === 2 ? (
                // Two primary buttons + cancel - all on same line
                <div className="flex gap-3 justify-center flex-wrap">
                  {wrappedButtons.map((button, index) => (
                    <FatOutlineButton
                      key={`${currentDialog.id}-action-${index}`}
                      color={button.color}
                      text={button.text}
                      minWidth={button.minWidth || "120px"}
                      onClick={button.onClick}
                    />
                  ))}
                  {showCancel && (
                    <FatOutlineButton
                      color="red"
                      text="Cancel"
                      minWidth="100px"
                      onClick={onCancel}
                    />
                  )}
                </div>
              ) : (
                // Multiple buttons - primary actions on top, cancel below
                <>
                  <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                    {wrappedButtons.map((button, index) => (
                      <FatOutlineButton
                        key={`${currentDialog.id}-action-${index}`}
                        color={button.color}
                        text={button.text}
                        minWidth={button.minWidth || "120px"}
                        onClick={button.onClick}
                      />
                    ))}
                  </div>
                  
                  {/* Cancel button separated for multiple options */}
                  {showCancel && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-3 w-full flex justify-center">
                      <FatOutlineButton
                        color="red"
                        text="Cancel"
                        minWidth="100px"
                        onClick={onCancel}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
});

// Utility factory functions

export const createDialogConfig = (
  id: string,
  title: string,
  content: React.ReactNode,
  buttons: DialogButton[],
  condition: boolean,
  showCancel?: boolean
): DialogConfig => ({
  id,
  title,
  content,
  buttons,
  condition,
  showCancel
});
